import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Divider } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SingleOperators } from 'common/src/query-params/operators';
import { BASE, NESTED_ROUTES } from 'common/src/routes/BaseRoutes';
import { generateNestedRoute } from 'common/src/routes/helpers';
import { IMeter, IPage } from 'common/src/types/interfaces';

import useAuth from 'portal/src//auth/useAuth';
import { getMeter, getMeters } from 'portal/src/api/MetersService';
import ContainerLoading from 'portal/src/components/common/ContainerLoading';
import DataTable from 'portal/src/components/common/DataTable';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import { BUTTON_TEXT } from 'portal/src/constants/button-text';

import buildAssociatedMetersColumns from './AssociatedMeters.columns';

import MeterForm from '../Form/Meters/MeterForm';

const AssociatedMeters = ({
  form,
  docId,
  isDisabled = false,
}: {
  form: 'account' | 'facility';
  docId: string;
  isDisabled: boolean;
}) => {
  const navigate = useNavigate();
  const { featureChecker } = useAuth();

  const [showAddMeterDialog, setShowAddMeterDialog] = useState(false);
  const [selectedMeterId, setSelectedMeterId] = useState<string | null>(null);
  const isAccountForm = form === 'account';

  const {
    data: associatedMeters,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['associatedMeters', form, docId],
    queryFn: async () => {
      const relationField =
        form === 'account' ? 'parentAccountId' : 'parentFacilityId';
      return await getMeters({
        filters: [
          {
            field: relationField,
            operator: SingleOperators.EQUALS,
            value: docId,
          },
        ],
      });
    },
    enabled: !!docId,
    initialData: { data: [] } as IPage<IMeter>,
  });

  const { data: selectedMeter, isFetching: isFetchingSelectedMeter } = useQuery(
    {
      queryKey: ['meterById', selectedMeterId],
      queryFn: async () => {
        if (!selectedMeterId) return null;

        return await getMeter(selectedMeterId);
      },
      enabled: !!selectedMeterId && showAddMeterDialog,
      initialData: null as IMeter | null,
    },
  );

  const handleCloseDialog = () => {
    setShowAddMeterDialog(false);
    setSelectedMeterId(null);
  };

  const handleMeterFormBackAction = async () => {
    await refetch();
    handleCloseDialog();
  };

  const handleEdit = (meterId: string) => {
    setSelectedMeterId(meterId);
    setShowAddMeterDialog(true);
  };

  const navigateToMeterDetails = (meterId: string) => {
    navigate(
      `${generateNestedRoute(BASE.ADMINISTRATION, NESTED_ROUTES.ADMINISTRATION.METERS)}/${meterId}`,
    );
  };

  return (
    <>
      <Divider sx={{ paddingTop: 2 }}>Associated Meters</Divider>
      <DataTable<Partial<IMeter>>
        actionsConfig={{
          readActionConfig: {
            onClick: (id) => navigateToMeterDetails(id),
          },
          extraActions: ({ id }) => [
            {
              disabled: isDisabled,
              icon: <EditIcon />,
              label: 'Edit Meter',
              onClick: () => handleEdit(id),
            },
          ],
        }}
        checkboxSelection={false}
        columns={buildAssociatedMetersColumns(form, featureChecker)}
        initialState={{
          sorting: { sortModel: [{ field: 'isActive', sort: 'desc' }] },
        }}
        isRowNavigation
        loading={isFetching}
        rows={associatedMeters.data}
        toolbarSlotProps={{
          children: (
            <Button
              color="secondary"
              disabled={isDisabled || isFetching}
              onClick={() => {
                setSelectedMeterId(null);
                setShowAddMeterDialog(true);
              }}
              startIcon={<AddIcon />}
              size="small"
              variant="outlined"
            >
              {BUTTON_TEXT.addMeter}
            </Button>
          ),
          showFilters: false,
        }}
        onRowDoubleClick={(params) => navigateToMeterDetails(params.row.id)}
      />
      <GenericDialog open={!!showAddMeterDialog} onClose={handleCloseDialog}>
        {selectedMeterId && isFetchingSelectedMeter ? (
          <ContainerLoading additionalLoadingText="meter" height="85vh" />
        ) : (
          <MeterForm
            backButtonlabel={BUTTON_TEXT.close}
            deactivatedFields={
              isAccountForm ? ['parentAccountId'] : ['parentFacilityId']
            }
            entity={
              selectedMeter ?? {
                parentAccountId: isAccountForm
                  ? docId
                  : ([...associatedMeters.data].pop()?.parentAccountId ?? null),
                parentFacilityId: !isAccountForm
                  ? docId
                  : ([...associatedMeters.data].pop()?.parentFacilityId ??
                    null),
              }
            }
            handleBackAction={handleMeterFormBackAction}
          />
        )}
      </GenericDialog>
    </>
  );
};

export default AssociatedMeters;
